import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type { CentinelaCaseMedia } from "../../../features/centinela/types";

/**
 * La evidencia de la dashcam para un caso: las fotos y los clips que el equipo
 * subió alrededor del hecho.
 *
 * Una fila con lo que grabó la cámara al conductor y la de carretera, con la
 * hora de cada archivo. Al hacer clic se abre a tamaño completo, que es cuando
 * el monitorista necesita ver el detalle; los clips se reproducen ahí mismo.
 *
 * Todo lo que se muestra aquí es material real del caso. Cuando no hay nada se
 * dice que no hay nada, en vez de ocultar la sección: un panel vacío se lee
 * como una falla del tablero, y lo que pasó es que la cámara no subió nada —
 * que es información operativa, no un error.
 */

const CAMERA_LABEL_KEY = {
  dms: "monitoreoIa.dashcam.cameras.dms",
  adas: "monitoreoIa.dashcam.cameras.adas",
} as const;

/** El equipo nombra sus canales por posición; la app los nombra por función. */
const CAMERA_FROM_DEVICE: Record<string, keyof typeof CAMERA_LABEL_KEY> = {
  front: "adas",
  out: "adas",
  adas: "adas",
  inward: "dms",
  in: "dms",
  dms: "dms",
};

const esVideo = (item: CentinelaCaseMedia) => item.kind === "video";

/**
 * Cuánto hace que se subió, en palabras.
 *
 * La hora exacta («14:38») obliga a restar mentalmente contra el reloj para
 * saber si es de ahora o de anoche, y eso es justo lo que se pregunta al mirar
 * una galería de evidencia. El dato exacto sigue estando en el `title`, para
 * quien lo necesite al pie de la letra.
 */
function desdeCuando(ts: number, t: (k: string, o?: Record<string, unknown>) => string) {
  const min = Math.max(0, Math.round((Date.now() - ts) / 60000));
  if (min < 1) return t("time.justNow", { defaultValue: "ahora" });
  if (min < 60) return t("time.minsAgo", { defaultValue: "hace {{n}} min", n: min });
  const h = Math.round(min / 60);
  if (h < 24) return t("time.hoursAgo", { defaultValue: "hace {{n}} h", n: h });
  const d = Math.round(h / 24);
  return t("time.daysAgo", { defaultValue: "hace {{n}} d", n: d });
}

export interface CaseDashcamShotsProps {
  media: CentinelaCaseMedia[];
  isLoading?: boolean;
  /** Qué encabezado poner. El mismo bloque sirve para la evidencia de un caso
   *  y para lo que grabó la unidad sin que haya caso. */
  titleKey?: string;
  /**
   * Carrusel en vez de rejilla.
   *
   * La evidencia de UN caso son dos o tres archivos y cabe en una rejilla. Lo
   * que grabó la unidad son decenas —25 en un día, con ADAS activo— y en
   * rejilla estiraba la tarjeta sin fin. En fila con desplazamiento lateral
   * ocupa una altura fija se muestre lo que se muestre.
   */
  carrusel?: boolean;
  /**
   * Acción de basura sobre cada archivo.
   *
   * Existe para el montón de «posible evidencia sin imagen»: son las mismas
   * miniaturas, el mismo carrusel y los mismos colores que el resto, y lo único
   * que cambia es que se pueden tirar. Reusar el componente en vez de hacer
   * otra rejilla es lo que mantiene que se vean igual — dos galerías distintas
   * se desincronizan a la primera que alguien retoca.
   */
  onDescartar?: (item: CentinelaCaseMedia) => void;
  descartando?: boolean;
  /**
   * Qué archivos se están yendo.
   *
   * Se desvanecen en el sitio en vez de desaparecer de golpe. Un elemento que
   * se esfuma sin transición no se lee como «se archivó»: se lee como que la
   * lista se recargó, y quien lo tocó se queda sin saber si su clic hizo algo.
   */
  saliendo?: (item: CentinelaCaseMedia) => boolean;
  /** Qué decir cuando no queda ninguna. */
  vacioKey?: string;
}

export default function CaseDashcamShots({
  media,
  isLoading,
  titleKey = "monitoreoIa.dashcam.lastShots",
  carrusel = false,
  onDescartar,
  descartando = false,
  saliendo,
  vacioKey,
}: CaseDashcamShotsProps) {
  const { t, i18n } = useTranslation("common");
  const [zoomed, setZoomed] = useState<CentinelaCaseMedia | null>(null);

  // Cuántas se traen por tanda en el carrusel. Doce llenan la fila con margen
  // para desplazar sin que el navegador tenga que pintar las veinticinco de
  // golpe, cada una con su `<video>` pidiendo metadatos.
  const POR_TANDA = 12;
  const [visibles, setVisibles] = useState(POR_TANDA);
  // ── El carrusel va al revés que la rejilla ────────────────────────────────
  //
  // La API devuelve la media en orden cronológico, y para la evidencia de UN
  // caso eso es lo correcto: cuenta la secuencia del hecho. Pero «lo que grabó
  // la cámara» es un feed, y ahí lo último es lo que importa: con la unidad
  // circulando, lo de hace tres horas queda al alcance solo después de
  // desplazar toda la fila.
  // ── Flechas, no barra de desplazamiento ───────────────────────────────────
  //
  // Siguiendo la guía de accesibilidad de Material 3: el carrusel se maneja con
  // botones etiquetados, que son alcanzables por teclado y anunciables por un
  // lector de pantalla. La barra nativa se conserva —el táctil la espera y
  // arrastrar sigue siendo lo natural en un teléfono—, pero deja de ser el
  // único modo de moverse.
  //
  // Los botones se deshabilitan en los extremos en vez de desaparecer: un
  // control que aparece y se va cambia el ancho de la fila y mueve lo que el
  // usuario estaba mirando.
  const pista = useRef<HTMLDivElement | null>(null);
  const [puedeIzq, setPuedeIzq] = useState(false);
  const [puedeDer, setPuedeDer] = useState(false);

  const revisarBordes = useCallback(() => {
    const el = pista.current;
    if (!el) return;
    setPuedeIzq(el.scrollLeft > 4);
    // El margen de 4 px absorbe el redondeo del navegador: sin él, el botón
    // derecho se queda habilitado al final y no hace nada al pulsarlo.
    setPuedeDer(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  const desplazar = (direccion: -1 | 1) => {
    const el = pista.current;
    if (!el) return;
    // Se avanza casi un ancho completo, dejando un resto visible: saltar la
    // pantalla entera hace perder el hilo de dónde se estaba.
    el.scrollBy({
      left: direccion * el.clientWidth * 0.8,
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
    });
  };

  const mostradas = carrusel
    ? [...media].sort((a, b) => b.ts - a.ts).slice(0, visibles)
    : media;

  useEffect(() => {
    const el = pista.current;
    if (!carrusel || !el) return undefined;

    revisarBordes();
    el.addEventListener("scroll", revisarBordes, { passive: true });
    // El ancho cambia al abrir y cerrar la tarjeta, y con él qué botones
    // aplican: sin observar el tamaño, quedan en el estado del primer render.
    const observador = new ResizeObserver(revisarBordes);
    observador.observe(el);

    return () => {
      el.removeEventListener("scroll", revisarBordes);
      observador.disconnect();
    };
  }, [carrusel, revisarBordes, mostradas.length]);

  const hora = (ts: number) =>
    new Date(ts).toLocaleTimeString(i18n.language, { hour: "2-digit", minute: "2-digit" });

  const etiquetaCamara = (item: CentinelaCaseMedia) => {
    const camera = CAMERA_FROM_DEVICE[String(item.camera ?? "").toLowerCase()];
    return camera ? t(CAMERA_LABEL_KEY[camera]) : t("monitoreoIa.dashcam.cameras.unknown");
  };

  return (
    <div className="border-t border-line pt-3">
      {/* Sin subtítulo cuando no aporta: «últimas capturas» prometía un orden
          —lo más reciente primero— que el carrusel ya no garantiza, y un rótulo
          que contradice lo que se ve debajo cuesta más que no estar. La
          antigüedad se dice ahora en cada miniatura, que es donde se necesita. */}
      {titleKey ? (
        <div className="mb-1.5 flex items-center gap-1.5">
          <i className="ri-camera-lens-line text-[11px] text-content-muted" />
          <p className="font-mono text-[9.5px] max-sm:text-[11px] uppercase tracking-widest text-content-muted">
            {t(titleKey)}
          </p>
        </div>
      ) : null}

      {isLoading ? (
        <p className="font-mono text-[10px] text-content-faint">
          {t("monitoreoIa.dashcam.loadingShots")}
        </p>
      ) : media.length === 0 ? (
        <p className="font-mono text-[10px] text-content-faint">
          {t(vacioKey || "monitoreoIa.dashcam.noShots")}
        </p>
      ) : (
        <div className={carrusel ? "group/car relative" : undefined}>
        {carrusel && (
          <>
            <button
              type="button"
              onClick={() => desplazar(-1)}
              disabled={!puedeIzq}
              aria-label={t("monitoreoIa.dashcam.anterior", { defaultValue: "Anterior" })}
              className="absolute left-0 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/60 p-1 max-sm:flex max-sm:h-11 max-sm:w-11 max-sm:items-center max-sm:justify-center text-white backdrop-blur transition-opacity disabled:pointer-events-none disabled:opacity-0"
            >
              <i className="ri-arrow-left-s-line text-lg" />
            </button>
            <button
              type="button"
              onClick={() => desplazar(1)}
              disabled={!puedeDer}
              aria-label={t("monitoreoIa.dashcam.siguiente", { defaultValue: "Siguiente" })}
              className="absolute right-0 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/60 p-1 max-sm:flex max-sm:h-11 max-sm:w-11 max-sm:items-center max-sm:justify-center text-white backdrop-blur transition-opacity disabled:pointer-events-none disabled:opacity-0"
            >
              <i className="ri-arrow-right-s-line text-lg" />
            </button>
          </>
        )}
        <div
          ref={pista}
          {...(carrusel
            ? { role: "group", "aria-roledescription": "carrusel", "aria-label": t(titleKey) }
            : {})}
          className={carrusel
            ? "flex snap-x snap-mandatory gap-1.5 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            : "grid grid-cols-2 gap-1.5"}
        >
          {mostradas.map((item) => (
            <div
              key={item.filename ?? `${item.camera}-${item.ts}`}
              /*
                Al salir NO basta con desvanecer: el hueco se queda y el
                carrusel deja un espacio en blanco donde estaba la foto. Se
                colapsa también el ANCHO en la misma transición, así la fila se
                cierra sola y las de al lado se recorren.

                El ancho se decide con un ternario y no con una clase que pise
                a `w-[46%]`: entre dos utilidades de Tailwind gana la que esté
                después en la hoja, no la que esté después en el atributo, así
                que «sobreescribir» aquí es una apuesta al orden de generación.
              */
              className={[
                "group relative overflow-hidden rounded-lg bg-black/40",
                "transition-all duration-300 motion-reduce:transition-none",
                carrusel ? "shrink-0 snap-start" : "",
                saliendo?.(item)
                  ? `scale-90 border-0 opacity-0 ${carrusel ? "w-0" : "h-0"}`
                  : `border border-line ${carrusel ? "w-[46%]" : ""}`,
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {/* La basura va SOBRE la miniatura y no debajo: así la tarjeta
                  mide lo mismo con o sin acción, y el carrusel no cambia de
                  alto entre un montón y otro. */}
              {onDescartar && (
                <button
                  type="button"
                  disabled={descartando}
                  onClick={(e) => { e.stopPropagation(); onDescartar(item); }}
                  title={t("monitoreoIa.evidencias.descartar", { defaultValue: "Descartar" })}
                  aria-label={t("monitoreoIa.evidencias.descartar", { defaultValue: "Descartar" })}
                  className="absolute right-1 top-1 z-10 rounded-md bg-black/60 p-1 max-sm:flex max-sm:h-10 max-sm:w-10 max-sm:items-center max-sm:justify-center text-white/80 backdrop-blur-sm transition-colors hover:bg-error hover:text-white disabled:opacity-40"
                >
                  <i className="ri-delete-bin-line text-[13px]" aria-hidden="true" />
                </button>
              )}
              <button
                type="button"
                onClick={() => setZoomed(item)}
                // La hora exacta no se pierde: vive en el tooltip, para quien
                // la necesite al pie de la letra.
                title={`${etiquetaCamara(item)} · ${hora(item.ts)}`}
                className="block w-full text-left"
              >
              {esVideo(item) ? (
                // `preload="metadata"` basta para pintar el primer cuadro: el
                // clip completo sólo se descarga si lo abren.
                <video
                  src={item.url}
                  preload="metadata"
                  muted
                  playsInline
                  className="aspect-video w-full object-cover transition-transform duration-200 group-hover:scale-[1.03]"
                />
              ) : (
                <img
                  src={item.url}
                  alt={etiquetaCamara(item)}
                  loading="lazy"
                  className="aspect-video w-full object-cover transition-transform duration-200 group-hover:scale-[1.03]"
                />
              )}

              <span className="absolute left-1 top-1 rounded bg-black/65 px-1 font-mono text-[8.5px] max-sm:text-[11px] uppercase tracking-wider text-white">
                {etiquetaCamara(item)}
              </span>

              {esVideo(item) ? (
                <span className="absolute inset-0 flex items-center justify-center">
                  <i className="ri-play-circle-fill text-2xl text-white/85 drop-shadow" />
                </span>
              ) : null}

              <span className="absolute bottom-1 right-1 rounded bg-black/65 px-1 font-mono text-[8.5px] max-sm:text-[11px] text-white">
                {desdeCuando(item.ts, t)}
              </span>
              </button>
            </div>
          ))}

          {carrusel && media.length > visibles && (
            <button
              type="button"
              onClick={() => setVisibles((n) => n + POR_TANDA)}
              className="flex w-[46%] shrink-0 snap-start flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-line bg-surface text-content-secondary transition-colors hover:bg-subtle"
            >
              <i className="ri-add-line text-lg" />
              <span className="font-mono text-[9px] max-sm:text-[11px] uppercase tracking-wider">
                {t("monitoreoIa.dashcam.verMas", {
                  defaultValue: "Ver más ({{restantes}})",
                  restantes: media.length - visibles,
                })}
              </span>
            </button>
          )}
        </div>
        </div>
      )}

      {zoomed ? (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-2 sm:p-6"
          onClick={() => setZoomed(null)}
        >
          {/*
            ── Por qué esto es una columna flexible y no un alto fijo ───────
            Antes el medio venía con `max-h-[70vh]` y el pie debajo, dentro de
            un `figure` con `max-h-full`. Son dos sistemas de medida
            distintos: el `70vh` mira la VENTANA y el `max-h-full` mira el
            diálogo, que además tiene `p-6`. En una pantalla baja —o con el
            navegador con zoom, que es lo mismo— 70vh más el pie más las dos
            franjas de relleno pasan del alto disponible, y el pie se sale por
            abajo. Se veía como un corte, no como un video.

            Ahora el medio no tiene alto propio: ocupa lo que sobre después
            del pie (`min-h-0 flex-1`) y `object-contain` lo encaja sin
            deformarlo. Funciona igual en cualquier resolución porque ya no
            hay ningún número que adivine el alto de la pantalla.
          */}
          <figure
            className="flex max-h-full min-h-0 w-full max-w-3xl flex-col"
            onClick={(event) => event.stopPropagation()}
          >
            {esVideo(zoomed) ? (
              <video
                src={zoomed.url}
                controls
                autoPlay
                playsInline
                className="min-h-0 w-full flex-1 rounded-lg bg-black object-contain"
              />
            ) : (
              <img
                src={zoomed.url}
                alt={etiquetaCamara(zoomed)}
                className="min-h-0 w-full flex-1 rounded-lg object-contain"
              />
            )}
            {/* `shrink-0`: el pie dice de qué cámara es y de cuándo. Si se
                encogiera para dejarle sitio al video, la evidencia quedaría
                sin identificar, que es lo único que no puede faltar. */}
            <figcaption className="mt-2 flex shrink-0 items-center justify-between font-mono text-[10px] uppercase tracking-wider text-white/80">
              <span>{etiquetaCamara(zoomed)}</span>
              <span>{new Date(zoomed.ts).toLocaleString(i18n.language)}</span>
            </figcaption>
          </figure>
        </div>
      ) : null}
    </div>
  );
}
